// Badge — status pill per COMPONENTS §Catalog + §Per-primitive WCAG.
//
// Two appearances (architect-locked — the
// Atlassian-lozenge subtle/bold model):
//
// - `subtle` (DEFAULT) — a soft 15%-tint of the Layer-3 semantic token + a 40%
//   border + normal text ink, a stronger cut of Alert's derivation (15% chip fill vs Alert's 10% banner fill — small chips need more tint than large banners), so banners and
//   pills read as one family. Calm by design: dense EMS lists hold many pills.
// - `bold` (OPT-IN, exceptional states only) — a solid DEEP fill
//   (--color-*-bold, ~700-grade) with one uniform white ink that clears WCAG
//   AA on every variant (ratios measured in index.css). Reserved for blocking
//   / exceptional / act-now states (Suspended, Expired, Failed) — at most one
//   bold pill per view region; everyday statuses stay subtle.
//
// The original solid recipe (mid-saturation fills + per-variant on-* inks) was
// retired: white fails AA on the green/amber fills, forcing a white/black ink
// split across variants that read as inconsistent. The on-* tokens remain in
// service for true mid-saturation solids (danger Button; the future X.733
// alarm-severity pill — a separate monitoring-domain primitive, NOT this
// Badge).
//
// Badge vs Tag: Badge says STATUS (semantic hues); Tag says a THING (neutral /
// brand only, removable machinery in TagGroup). Same calm pill shape.
//
// Status must never be conveyed by colour alone (WCAG SC 1.4.1): the label text
// IS the signal; the fill reinforces it.

import type { ReactNode } from 'react';

export type BadgeVariant = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeAppearance = 'subtle' | 'bold' | 'dot';

export interface BadgeProps {
  variant?: BadgeVariant;
  /** `subtle` (default) for everyday statuses; `bold` for blocking/exceptional ones;
   *  `dot` for SPACE-STARVED chrome (the condensed entity bar — architect-ruled at
   *  the asset detail design round): a 10px disc in the variant's BOLD fill, children demoted
   *  to sr-only. The 1.4.1 "never colour alone" rule is honoured by proximity, not
   *  the pill: the full-text Badge is one scroll away in the full header, the word
   *  stays readable to AT (sr-only) and on hover (`title`). Callers pass `title`. */
  appearance?: BadgeAppearance;
  /** The status text (caller-supplied, i18n'd) — and optional leading icon. */
  children: ReactNode;
  /** Hover word for the `dot` appearance (the visible text is sr-only there). */
  title?: string;
  className?: string;
}

const subtleClass: Record<BadgeVariant, string> = {
  neutral: 'bg-[var(--color-bg-surface-alt)] border-[var(--color-border)] text-[var(--color-text-primary)]',
  // Same derivation as the semantic subtles (15% fill / 40% border of the
  // DISPLAY value) — never the focus-ring token (a focus indicator, solid
  // since ADR-032; reusing it here turned subtle borders full-strength).
  brand:
    'bg-[var(--color-primary-display)]/15 border-[var(--color-primary-display)]/40 text-[var(--color-primary-display)]',
  success:
    'bg-[var(--color-success)]/15 border-[var(--color-success)]/40 text-[var(--color-text-primary)]',
  warning:
    'bg-[var(--color-warning)]/15 border-[var(--color-warning)]/40 text-[var(--color-text-primary)]',
  danger:
    'bg-[var(--color-danger)]/15 border-[var(--color-danger)]/40 text-[var(--color-text-primary)]',
  info: 'bg-[var(--color-info)]/15 border-[var(--color-info)]/40 text-[var(--color-text-primary)]',
};

const boldClass: Record<BadgeVariant, string> = {
  neutral: 'bg-[var(--color-neutral-bold)] border-transparent text-[var(--color-on-bold)]',
  brand: 'bg-[var(--color-primary-display)] border-transparent text-[var(--color-on-primary-display)]',
  success: 'bg-[var(--color-success-bold)] border-transparent text-[var(--color-on-bold)]',
  warning: 'bg-[var(--color-warning-bold)] border-transparent text-[var(--color-on-bold)]',
  danger: 'bg-[var(--color-danger-bold)] border-transparent text-[var(--color-on-bold)]',
  info: 'bg-[var(--color-info-bold)] border-transparent text-[var(--color-on-bold)]',
};

// Dot fills ride the BOLD (~700-grade) tokens: a 10px disc must clear WCAG
// 1.4.11 (3:1 non-text contrast) on canvas AND chrome; the subtle 15% tints
// cannot.
const dotClass: Record<BadgeVariant, string> = {
  neutral: 'bg-[var(--color-neutral-bold)]',
  brand: 'bg-[var(--color-primary-display)]',
  success: 'bg-[var(--color-success-bold)]',
  warning: 'bg-[var(--color-warning-bold)]',
  danger: 'bg-[var(--color-danger-bold)]',
  info: 'bg-[var(--color-info-bold)]',
};

export function Badge({
  variant = 'neutral',
  appearance = 'subtle',
  children,
  title,
  className = '',
}: BadgeProps) {
  if (appearance === 'dot') {
    return (
      <span className={`inline-flex items-center ${className}`} title={title}>
        <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotClass[variant]}`} />
        <span className="sr-only">{children}</span>
      </span>
    );
  }
  const look = appearance === 'bold' ? boldClass[variant] : subtleClass[variant];
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${look} ${className}`}
    >
      {children}
    </span>
  );
}
